import { HttpClient, HttpContext, HttpRequest } from '@angular/common/http';
import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { lazyInject } from '@app/shared/decorators';
import { IServiceConfig, ServiceIdentifyType } from './service-config.type';
import { BackendApiType } from '@app/shared/types/enums/backend-api-type.enum';

// import { buildServiceIdentifyContext } from '@app/shared/utils';
import { head, isFunction, merge, set } from 'lodash';
import { buildServiceIdentifyContext } from '@app/shared/utils/http-context.utils';
import { IHttpRequestOptions } from '@app/shared/types/common/http.types';

@Injectable()
export class ServiceWithCustomHttp {
  /**
   * Static config for the service
   * You need to override it in the derived class instead of instance config
   */
  public static config: IServiceConfig;

  public static BACKEND_API_TYPE: BackendApiType;

  public serviceIdentifyType: ServiceIdentifyType;

  /**
   * Instance config for the service
   * You can override it in the derived class constructor if needed
   * You should not reassign assign the config in the derived class field directly
   */
  public config: IServiceConfig;

  @lazyInject(HttpClient)
  protected _httpClient: HttpClient;

  public get http(): HttpClient {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const host = this;
    const hostConfig = this.config;

    return new Proxy(this._httpClient, {
      get: (target, prop) => {
        if (isFunction(target[prop])) {
          return (...args) => {
            const method = String(prop).toLowerCase();

            const firstParam = head(args);
            // Handle special case for request(HttpRequest)
            if (
              method === 'request' &&
              firstParam instanceof HttpRequest &&
              args.length === 1 &&
              firstParam?.url
            ) {
              const req = firstParam;

              const context = req.context ?? new HttpContext();
              const callerIdentifier = hostConfig?.serviceIdentifyType;

              args[0] = req.clone({
                context: buildServiceIdentifyContext(callerIdentifier, {
                  context,
                  specificI18nConfig: hostConfig?.i18nConfig,
                }),
              });

              return target[prop](...args);
            }

            // Determine options parameter position
            let optionsIndex: number;
            if (['post', 'put', 'patch', 'request'].includes(method)) {
              optionsIndex = 2; // (url, body, options?)
            } else if (['get', 'delete', 'head', 'options'].includes(method)) {
              optionsIndex = 1; // (url, options?)
            } else {
              // Unknown method, use last parameter
              optionsIndex = args.length - 1;
            }

            // Ensure optionsIndex is valid
            if (optionsIndex < 0) {
              return target[prop](...args);
            }

            // Get or create options
            const reqOptions = args[optionsIndex];

            // Check if it's a valid options object
            const isValidOptions =
              reqOptions &&
              typeof reqOptions === 'object' &&
              !Array.isArray(reqOptions);

            if (isValidOptions) {
              args[optionsIndex] = host.setServiceIdentifyCtx.call(
                host,
                reqOptions,
                hostConfig,
              );
            } else {
              // If not exists or not a valid options, create a new one
              args[optionsIndex] = host.setServiceIdentifyCtx.call(
                host,
                {},
                hostConfig,
              );
            }

            return target[prop](...args);
          };
        } else {
          return target[prop];
        }
      },
    });
  }

  public constructor(public injector: Injector) {
    this.config = merge<
      Partial<IServiceConfig>,
      IServiceConfig,
      IServiceConfig
    >({}, this.config, new.target.config);

    const identifyType =
      this.config?.serviceIdentifyType ??
      this.config?.ENTITY_TYPE ??
      (this as { constructor?: { name?: string } }).constructor?.name;

    set(this.config, 'serviceIdentifyType', identifyType);

    this.serviceIdentifyType = identifyType;
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  /**
   * use to set service identify in http request context
   *
   * @param options
   * @param hostConfig
   * @returns
   */
  protected setServiceIdentifyCtx(
    options: IHttpRequestOptions,
    hostConfig: IServiceConfig,
  ): IHttpRequestOptions {
    // Inject caller information
    const callerIdentify =
      hostConfig?.serviceIdentifyType ?? this.serviceIdentifyType;

    const i18nConfig = hostConfig?.i18nConfig;

    const context = buildServiceIdentifyContext(callerIdentify, {
      context: options.context,
      specificI18nConfig: i18nConfig,
    });

    options.context = context;

    return options;
  }
}
